import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { getUuid } from '@front10/helpers/dist/uuid';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import ImageHeader from '../../elements/ImageHeader';
import Header from '../../elements/Header';
import Box from '../../elements/Box';
import Button from '../../elements/Button';
import FareListTwoColumns from '../../lists/FareListTwoColumns';
import FareItemMediumSize from '../FareItemMediumSize';
import Link from '../../elements/Link';
import ItemContainer from './atoms/ItemContainer';
import * as logger from 'loglevel';
import axios from 'axios';

const DestinationItem = (props) => {
  const {
    imageProps,
    image,
    name,
    iataCode,
    fares,
    fareListTwoColumns,
    header,
    showImage,
    onClickSeeMore,
    onKeyDown,
    columnNumbers,
    showCityName,
    focusReferences,
    seeMore,
    showSeeMore,
    showHeaderBreak,
    siteEdition,
    seeMoreBasicUrl,
    traffixVariant,
    focusable,
    oneColumn,
    focusClass,
    layout,
    integrationSettings,
    enableTrfxRedirectIntegration = false,
    seeMoreTrfxRedirectSettings = {}
  } = props;
  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const id = getUuid();
  const origin = fares[0] ? fares[0].origin : '';
  const destinationCity = fares[0] && fares[0].destinationCity ? fares[0].destinationCity : name;
  const imageContainerSettings =
    layout === 'fluid'
      ? { ...imageProps.containerSettings, display: 'flex' }
      : imageProps.containerSettings;

  const { showPrepositionLabel = false } = header;

  function getRedirectUrl(
    origin,
    destination,
    siteEdition,
    fares,
    trfxRedirectSettings,
    integrationSettings
  ) {
    return new Promise((resolve, reject) => {
      const flightType = fares[0] && fares[0]?.flightType ? fares[0]?.flightType : '';
      const airlineCode = fares[0] && fares[0]?.airlineCode ? fares[0]?.airlineCode : '';
      const templateType =
        trfxRedirectSettings?.templateType && trfxRedirectSettings?.templateType !== ''
          ? trfxRedirectSettings?.templateType
          : 'CI';
      const bookingType =
        trfxRedirectSettings?.bookingType && trfxRedirectSettings?.bookingType !== ''
          ? trfxRedirectSettings?.bookingType
          : 'TRFX';
      const domainType =
        trfxRedirectSettings?.domainType && trfxRedirectSettings?.domainType !== ''
          ? trfxRedirectSettings?.domainType
          : 'DEV';
      const trackingParameters = trfxRedirectSettings?.trackingParameters
        ? trfxRedirectSettings?.trackingParameters
        : undefined;

      const query = {
        origin,
        destination,
        siteEdition,
        templateType,
        bookingType,
        domainType,
        flightType
      };
      if (trackingParameters) {
        query['trackingParameters'] = trackingParameters;
      }
      const { v2DeepLinkApiSettings } = integrationSettings;
      let error = '';
      if (!v2DeepLinkApiSettings.method) {
        error += 'INTEGRATION__DEEPLINK: **method** is not defined. ';
      }
      if (!v2DeepLinkApiSettings.endPoint) {
        error += 'INTEGRATION__DEEPLINK: **endPoint** is not defined.';
      }
      if (
        v2DeepLinkApiSettings.endPoint.search('%%airlineCode%%') !== -1 ||
        v2DeepLinkApiSettings.endPoint.search('@@airlineIataCode@@') !== -1
      ) {
        v2DeepLinkApiSettings.endPoint = v2DeepLinkApiSettings.endPoint.replace(
          '%%airlineCode%%',
          airlineCode
        );
        v2DeepLinkApiSettings.endPoint = v2DeepLinkApiSettings.endPoint.replace(
          '@@airlineIataCode@@',
          airlineCode
        );
      }
      if (!v2DeepLinkApiSettings.headers) {
        v2DeepLinkApiSettings.headers = {};
      }
      if (error) {
        logger.error(error);
      } else {
        axios({
          method: v2DeepLinkApiSettings.method,
          url: `${v2DeepLinkApiSettings.endPoint}`,
          headers: { ...v2DeepLinkApiSettings.headers },
          data: query
        })
          .then((response) => {
            if (response?.data?.url) {
              resolve(response?.data?.url);
            } else {
              reject('');
            }
          })
          .catch((err) => {
            reject('');
            logger.error(err);
          });
      }
    });
  }

  const handleClickSeeMore = () => {
    onClickSeeMore(fares[0]);
    getRedirectUrl(
      origin,
      iataCode,
      siteEdition,
      fares,
      seeMoreTrfxRedirectSettings,
      integrationSettings
    ).then((url) => {
      window.open(url, '_top');
    });
  };

  return (
    <Box
      focusable={focusable}
      tabIndex={focusable ? 0 : null}
      className={`${className} fr-destination-item`}
      style={style}
      focusReferences={focusReferences}
      onKeyDown={onKeyDown}
      dataTestId="destinationItem"
      focusClass={focusClass}
    >
      {showImage && (
        <ImageHeader
          {...imageProps}
          imageHeight={layout === 'fluid' ? 'auto' : imageProps.imageHeight}
          containerSettings={imageContainerSettings}
          image={image}
          alt={destinationCity}
        >
          <ItemContainer {...imageProps.container} text={name} subText={iataCode} />
        </ImageHeader>
      )}
      {showCityName && (
        <Header
          id={id}
          customHtml={
            !showPrepositionLabel
              ? `${translate('cmp-destination-item-flights-to', 'Flights to')}${
                  showHeaderBreak ? '<br />' : ''
                } ${name}  (${iataCode})`
              : `${translate('cmp-destination-item-flights-to', 'Flights to')}${
                  showHeaderBreak ? '<br />' : ''
                } ${name}  (${iataCode}) ${translate(
                  'cmp-preposition-after-destination-header-label',
                  'Flights to'
                )}`
          }
          {...header}
          tag="h3"
        />
      )}
      <FareListTwoColumns
        {...fareListTwoColumns}
        columnNumbers={columnNumbers}
        ItemComponentInstance={FareItemMediumSize}
        itemsData={fares}
        oneColumn={oneColumn}
        ariaLabelledBy={showCityName ? id : null}
      />
      {enableTrfxRedirectIntegration ? (
        <>
          {showSeeMore && siteEdition && (
            <Box display="flex" justifyContent="center">
              <Button
                className="fr-see-more-link"
                role="link"
                onClick={handleClickSeeMore}
                {...seeMore}
              >
                {translate('cmp-destination-item-see-more', 'See more')}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <>
          {showSeeMore && siteEdition && seeMoreBasicUrl && (
            <Box display="flex" justifyContent="center">
              <Link
                onClick={() => onClickSeeMore(fares[0])}
                {...seeMore}
                href={`${seeMoreBasicUrl}/redirect?edition=${siteEdition}&orig=${origin}&dest=${iataCode}&tc=${traffixVariant}`}
              >
                {translate('cmp-destination-item-see-more', 'See more')}
              </Link>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

DestinationItem.propTypes = {
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Image of destination.
   */
  image: PropTypes.string,
  /**
   * image props.
   */
  imageProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Name of destination.
   */
  name: PropTypes.string,
  /**
   * iataCode of destination.
   */
  iataCode: PropTypes.string,
  /**
   * list of fares.
   */
  fares: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  /**
   * Component that is going to render
   */
  fareListTwoColumns: PropTypes.objectOf(PropTypes.any),
  /**
   * Header props
   */
  header: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the image will be displayed in the component.
   */
  showImage: PropTypes.bool,
  /**
   * Function to translate the component
   */
  columnNumbers: PropTypes.number,
  /**
   * Show or hide the city name
   */
  showCityName: PropTypes.bool,
  /**
   * Handle to be called when seeMore is Clicked
   */
  onClickSeeMore: PropTypes.func,
  /**
   * OnKeyDown function.
   */
  onKeyDown: PropTypes.func,
  /**
   * FocusReferences functions
   */
  focusReferences: PropTypes.func,
  /**
   * Turn off or on tabIndex in the DestinationItem
   */
  focusable: PropTypes.bool,
  /**
   * Settings of seeMore component
   */
  seeMore: PropTypes.objectOf(PropTypes.any),
  /**
   * Show or hide see more
   */
  showSeeMore: PropTypes.bool,
  /**
   * Show header in two lines
   */
  showHeaderBreak: PropTypes.bool,
  /**
   * Site edition of the component
   */
  siteEdition: PropTypes.string,
  /**
   * Url base for component see more
   */
  seeMoreBasicUrl: PropTypes.string,
  /**
   * Traffix variants
   */
  traffixVariant: PropTypes.string,
  /**
   * Show list in one column
   */
  oneColumn: PropTypes.bool,
  /**
   * Class to apply focus
   */
  focusClass: PropTypes.string,
  layout: PropTypes.oneOf(['fixed', 'fluid'])
};
DestinationItem.defaultProps = {
  image: '',
  name: '',
  iataCode: '',
  fares: [],
  imageProps: {},
  fareListTwoColumns: {},
  header: {},
  showImage: true,
  showCityName: true,
  showHeaderBreak: false,
  columnNumbers: null,
  onClickSeeMore: null,
  onKeyDown: () => {},
  focusReferences: () => {},
  focusable: false,
  seeMore: {
    display: 'inline-block',
    textColor: 'dark',
    fontWeight: 'bold',
    padding: '2'
  },
  showSeeMore: true,
  siteEdition: null,
  seeMoreBasicUrl: null,
  traffixVariant: 'ci',
  oneColumn: true,
  focusClass: null,
  layout: 'fixed'
};
DestinationItem.componentName = 'DestinationItem';

export default DestinationItem;
